/**
 * Bugs list filters <-> URL search params (ADMIN_FLOW.md §8.3), so a
 * filtered view is a shareable link and survives a refresh.
 */
import type { BugListParams } from '../adminApi'

export const SORT_KEYS = ['created_at', 'updated_at', 'reference_code', 'status', 'module', 'severity', 'assignee'] as const
export type SortKey = (typeof SORT_KEYS)[number]

export interface BugFilters {
  status: string
  module: string
  severity: string
  assignee: string
  q: string
  sort: SortKey
  order: 'asc' | 'desc'
  page: number
}

export const DEFAULT_FILTERS: BugFilters = {
  status: '',
  module: '',
  severity: '',
  assignee: '',
  q: '',
  sort: 'created_at',
  order: 'desc',
  page: 1,
}

export const PAGE_SIZE = 25

export function filtersFromSearch(search: string | URLSearchParams): BugFilters {
  const p = typeof search === 'string' ? new URLSearchParams(search) : search
  const sort = p.get('sort') ?? ''
  const order = p.get('order')
  const page = Number.parseInt(p.get('page') ?? '', 10)
  return {
    status: p.get('status') ?? '',
    module: p.get('module') ?? '',
    severity: p.get('severity') ?? '',
    assignee: p.get('assignee') ?? '',
    q: p.get('q') ?? '',
    sort: (SORT_KEYS as readonly string[]).includes(sort) ? (sort as SortKey) : DEFAULT_FILTERS.sort,
    order: order === 'asc' || order === 'desc' ? order : DEFAULT_FILTERS.order,
    page: Number.isFinite(page) && page >= 1 ? page : 1,
  }
}

/** Only non-default values are written, so the default view has a clean URL. */
export function searchFromFilters(f: BugFilters): URLSearchParams {
  const p = new URLSearchParams()
  if (f.status) p.set('status', f.status)
  if (f.module) p.set('module', f.module)
  if (f.severity) p.set('severity', f.severity)
  if (f.assignee) p.set('assignee', f.assignee)
  if (f.q) p.set('q', f.q)
  if (f.sort !== DEFAULT_FILTERS.sort) p.set('sort', f.sort)
  if (f.order !== DEFAULT_FILTERS.order) p.set('order', f.order)
  if (f.page > 1) p.set('page', String(f.page))
  return p
}

export function toListParams(f: BugFilters): BugListParams {
  return {
    status: f.status || undefined,
    module: f.module || undefined,
    severity: f.severity || undefined,
    assignee: f.assignee || undefined,
    q: f.q || undefined,
    sort: f.sort,
    order: f.order,
    page: f.page,
    page_size: PAGE_SIZE,
  }
}

/** Clicking a column header: same key flips direction, a new key starts descending
 *  for dates/severity and ascending for text columns. Always returns to page 1. */
export function toggleSort(f: BugFilters, key: SortKey): BugFilters {
  if (f.sort === key) {
    return { ...f, order: f.order === 'asc' ? 'desc' : 'asc', page: 1 }
  }
  const textual: SortKey[] = ['reference_code', 'status', 'module', 'assignee']
  return { ...f, sort: key, order: textual.includes(key) ? 'asc' : 'desc', page: 1 }
}

export function hasActiveFilters(f: BugFilters): boolean {
  return Boolean(f.status || f.module || f.severity || f.assignee || f.q)
}

export function pageCount(total: number, pageSize = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / pageSize))
}
